/* import __COLOCATED_TEMPLATE__ from './form.hbs'; */
import { action } from '@ember/object';
import { bannerError } from 'client-portal/utils/error-handling';
import { formatPhoneNumber } from 'client-portal/utils/format-phone-number';
import {
  formatValidationErrorsForMixpanel,
  scrollToValidationError,
} from 'client-portal/utils/validate-record';
import { gte, reads } from 'macro-decorators';
import { service } from '@ember/service';
import { success } from 'client-portal/utils/banners';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { waitForRender } from 'ember-simplepractice/utils/waiters';
import Component from '@glimmer/component';
import ENV from 'client-portal/config/environment';
import stateGroups from 'ember-simplepractice/utils/state-groups';
import styles from './form.module.scss';

export default class SiteBillingInsuranceForm extends Component {
  @service router;
  @service store;
  @service mixpanel;

  styles = styles;
  stateGroups = stateGroups;

  @tracked isLoading = false;
  @tracked isReview = false;

  @reads('args.client') client;
  @reads('args.insuranceInfo') insurance;
  @gte('insuranceCards.length', 1) showInsuranceCardBack;

  get sexOptions() {
    return ['Male', 'Female'];
  }

  get insuredRelations() {
    return {
      '0': 'Self',
      '1': 'Spouse',
      '2': 'Parent',
      '3': 'Other',
    };
  }

  get insuredRelationText() {
    return this.insuredRelations[this.insurance?.insuredRelation] || 'Self';
  }

  get showInsuredDetails() {
    return this.insurance.insuredRelation !== '0';
  }

  get insuranceCards() {
    return (
      this.insurance?.documents?.filter(doc => doc.constructor.modelName === 'insurance-card') || []
    );
  }

  get insuranceCardFront() {
    return this.insuranceCards.slice().at(0);
  }

  get insuranceCardBack() {
    return this.insuranceCards.slice().at(1);
  }

  get isValid() {
    return (
      this.insurance.insurancePlan &&
      this.insurance.memberId &&
      this.client.sex &&
      this.insurance.insuredRelation !== null &&
      (!this.showInsuredDetails ||
        (this.insurance.insuredFname &&
          this.insurance.insuredLname &&
          this.insurance.insuredDob &&
          this.insurance.insuredPhone &&
          this.insurance.insuredAddress &&
          this.insurance.insuredCity &&
          this.insurance.insuredState &&
          this.insurance.insuredZip))
    );
  }

  @action
  toggleReview() {
    this.isReview = !this.isReview;
  }

  @action
  async handleSubmit() {
    if (!this.isReview) {
      if (!this.isValid) {
        await this.validateForm();
        return;
      }
      this.isReview = true;
    } else {
      try {
        this.isLoading = true;
        await this.saveInsuranceTask.perform();
        success({ title: 'Insurance information saved successfully' });
        this.router.transitionTo('site.billing.insurance');
      } catch (error) {
        bannerError('Failed to save insurance information. Please try again.');
      } finally {
        this.isLoading = false;
      }
    }
  }

  async validateForm() {
    this.insurance.validate();
    this.client.validate();

    this.insurance.set('isDirty', !this.insurance.validations.isValid);
    this.client.set('isDirty', !this.client.validations.isValid);

    if (this.insurance.isDirty || this.client.isDirty) {
      this.#trackValidationErrors();
      await waitForRender();
      scrollToValidationError();
    }
  }

  @task
  *saveInsuranceTask() {
    this.insurance.set('thisType', 'other');

    // Format phone number
    if (this.insurance.insuredPhone) {
      this.insurance.set('insuredPhone', formatPhoneNumber(this.insurance.insuredPhone));
    }

    yield this.insurance.save();
  }

  @(task(function* (search) {
    return this.store.query('insurance-provider-name', {
      filter: {
        search,
      },
      page: {
        size: ENV.maxPageSize,
      },
    });
  }).restartable())
  searchInsuranceProviderNamesTask;

  #trackValidationErrors() {
    if (this.insurance.validations?.errors?.length) {
      let errors = formatValidationErrorsForMixpanel(this.insurance.validations.errors);
      this.mixpanel.track('client: insurance form validation error', { errors });
    }
  }
}
